import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon } from '../icons';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { createSlug } from '../../../../util/urlHelpers';
import { initElbScope } from '../../../../analytics/trackElbAction';
import routeConfiguration from '../../../../routing/routeConfiguration';
import { pathByRouteName } from '../../../../util/routes';
import {
  convertMoneyToNumber,
  formatPriceFromUnitPurchaseExperience,
} from '../../../../util/currency';
import config from '../../../../config/configDefault';
import {
  currencyCheck,
  getGuestsLabel,
  getPricePer,
  getTranslatedMDELabel,
} from '../AccomodationDetails/DifferentRoomtypesSlider/helpers';
import css from './OtherListings.module.css';

const isExperienceType = listing =>
  listing?.attributes?.publicData?.listingType === 'impact-experience';

const roomTypeIdMap = {
  singlebedroom: 'roomtypes.singlebedroom',
  doublebedroom: 'roomtypes.doublebedroom',
  twobedroom: 'roomtypes.twobedroom',
  shared_bedroom: 'roomtypes.shared_bedroom',
  multi_bedroom: 'roomtypes.multi_bedroom',
  entire_accomodation: 'roomtypes.entire_accomodation',
  tent: 'roomtypes.tent',
};

const OtherListings = ({ listingsItems }) => {
  const intl = useIntl();
  const { lang } = useParams();
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateScrollButtons = useCallback(() => {
    const container = scrollRef.current;
    if (!container) {
      return;
    }

    setCanScrollLeft(container.scrollLeft > 0);
    setCanScrollRight(container.scrollLeft + container.clientWidth < container.scrollWidth - 1);
  }, []);

  useEffect(() => {
    updateScrollButtons();
    const container = scrollRef.current;
    if (!container) {
      return undefined;
    }

    container.addEventListener('scroll', updateScrollButtons);
    window.addEventListener('resize', updateScrollButtons);

    return () => {
      container.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, [listingsItems, updateScrollButtons]);

  const scrollByCard = direction => {
    const container = scrollRef.current;
    if (!container) {
      return;
    }

    const card = container.querySelector(`.${css.card}`);
    if (!card) {
      return;
    }

    const gap = parseInt(window.getComputedStyle(container).gap, 10) || 13;
    container.scrollBy({
      left: direction * (card.offsetWidth + gap),
      behavior: 'smooth',
    });
  };

  const getPriceData = price => {
    if (price && price.currency === config.currency) {
      const formattedPrice = intl.formatNumber(convertMoneyToNumber(price), {
        style: 'currency',
        currency: price.currency,
        currencyDisplay: 'symbol',
        useGrouping: true,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
      return { formattedPrice, priceTitle: formattedPrice };
    }
    if (price) {
      return {
        formattedPrice: intl.formatMessage(
          { id: 'ListingCard.unsupportedPrice' },
          { currency: price.currency }
        ),
        priceTitle: intl.formatMessage(
          { id: 'ListingCard.unsupportedPriceTitle' },
          { currency: price.currency }
        ),
      };
    }
    return {};
  };

  // The section is gated on the listings arriving, so it mounts after Walker has
  // scanned the page — its `visible` trigger has to be registered by hand.
  const sectionRef = useRef(null);
  useEffect(() => {
    initElbScope(sectionRef.current);
  }, [listingsItems]);

  if (!listingsItems || listingsItems.length < 2) {
    return null;
  }

  return (
    <section ref={sectionRef} className={css.section} data-elbaction="visible:vis otherListings">
      <div className={css.header}>
        <h2 className={css.title}>
          <FormattedMessage id="OtherListings.title" />
        </h2>

        {listingsItems.length > 1 && (
          <div className={css.navButtons}>
            <button
              type="button"
              className={`${css.navButton}${canScrollLeft ? '' : ` ${css.navButtonHidden}`}`}
              onClick={() => scrollByCard(-1)}
              disabled={!canScrollLeft}
              aria-label={intl.formatMessage({ id: 'PaginationLinks.previous' })}
            >
              <ChevronLeftIcon
                className={css.navIcon}
                size={22}
                strokeWidth={2}
                aria-hidden="true"
              />
            </button>
            <button
              type="button"
              className={css.navButton}
              onClick={() => scrollByCard(1)}
              disabled={!canScrollRight}
              aria-label={intl.formatMessage({ id: 'PaginationLinks.next' })}
            >
              <ChevronRightIcon
                className={css.navIcon}
                size={22}
                strokeWidth={2}
                aria-hidden="true"
              />
            </button>
          </div>
        )}
      </div>

      <div className={css.scrollContainer} ref={scrollRef}>
        {listingsItems.map((listing, i) => {
          const isExperience = isExperienceType(listing);
          const { price, publicData } = listing?.attributes || {};
          const { formattedPrice, priceTitle } = getPriceData(price);
          const id = listing?.id?.uuid;
          const slug = createSlug(listing?.attributes?.title);
          const href = pathByRouteName('ListingPage', routeConfiguration(), { lang, id, slug });

          const experienceTitle =
            intl.locale === 'en' ? publicData?.experienceTitle : publicData?.experienceTitleDe;
          const stayTitle =
            intl.locale === 'en' ? publicData?.stayTitle : publicData?.stayTitleDe;
          const title = isExperience
            ? experienceTitle
            : stayTitle || listing.attributes?.title?.split('•')[0];

          const subtitle = isExperience
            ? getTranslatedMDELabel(listing, intl)
            : getGuestsLabel(listing, intl);

          const imageUrl =
            listing?.images?.[0]?.attributes?.variants?.['landscape-crop2x']?.url || '';

          const roomtype = publicData?.roomtype;
          const roomTypeMsgId = roomTypeIdMap[roomtype];

          return (
            <a
              key={`${id}-${i}`}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className={css.card}
            >
              <div className={css.imageWrap}>
                {imageUrl ? (
                  <img src={imageUrl} alt="" className={css.image} loading="lazy" />
                ) : (
                  <div className={css.imagePlaceholder}>
                    {roomTypeMsgId ? (
                      <FormattedMessage id={roomTypeMsgId} />
                    ) : (
                      <FormattedMessage id="MainPanelHeader.listingTypesFilter.Stay" />
                    )}
                  </div>
                )}
              </div>

              <div className={css.content}>
                <div className={css.cardTitle}>{title}</div>
                <div className={css.subtitle}>{subtitle}</div>
                <div className={css.priceRow}>
                  {isExperience ? (
                    <FormattedMessage
                      id="listingPage.experiencePriceLabel"
                      values={{
                        value: formatPriceFromUnitPurchaseExperience(intl, price?.amount),
                      }}
                    />
                  ) : (
                    <>
                      <span className={css.priceValue} title={priceTitle}>
                        {currencyCheck(publicData?.customCurrency, formattedPrice)}
                      </span>
                      <span className={css.priceUnit}>
                        {publicData?.roomtype === 'shared_bedroom' ? (
                          <> / person</>
                        ) : (
                          <>
                            / <FormattedMessage id="Slider.night" /> {getPricePer(roomtype)}
                          </>
                        )}
                      </span>
                    </>
                  )}
                </div>
              </div>

              <ChevronRightIcon className={css.arrow} size={22} strokeWidth={2} aria-hidden="true" />
            </a>
          );
        })}
      </div>
    </section>
  );
};

export default OtherListings;
